import type {
  StrapiMagazineArticleCategory,
  StrapiMagazineArticleCategoryLocalization,
} from '~/composables/types/strapi/strapi-magazine'
import { articleCategoryGetters } from '~/utils/articleCategoryGetters'

function getLocalizationSlug(
  loc: StrapiMagazineArticleCategoryLocalization | undefined
): string | undefined {
  if (!loc) return undefined
  const slug =
    loc.slug ?? (loc as { attributes?: { slug?: string } }).attributes?.slug
  return slug ?? undefined
}

export const useMagazineCategoryLocaleSwitcher = (
  category: StrapiMagazineArticleCategory | null | undefined
) => {
  const buildLocaleUrl = (targetLocale: string): string | null => {
    if (!category) return null

    const normalizedTargetLocale = targetLocale.toLowerCase()
    const currentLocale = articleCategoryGetters
      .getLocale(category)
      ?.toLowerCase()

    if (currentLocale === normalizedTargetLocale) return null

    const localizations = articleCategoryGetters.getLocalizations(category)

    if (!localizations?.length) return null

    const targetLocalization = localizations.find(
      (loc) => loc.locale?.toLowerCase() === normalizedTargetLocale
    )

    const slug = targetLocalization
      ? getLocalizationSlug(targetLocalization)
      : undefined
    if (!slug) return null

    const localePrefix = targetLocale.toLowerCase()
    return `/${localePrefix}/magazine/${slug}/`
  }

  const hasOtherLocales = (): boolean => {
    if (!category) return false
    const localizations = articleCategoryGetters.getLocalizations(category)
    if (!localizations?.length) return false
    const currentLocale = articleCategoryGetters
      .getLocale(category)
      ?.toLowerCase()
    return localizations.some(
      (loc) => loc.locale?.toLowerCase() !== currentLocale
    )
  }

  return {
    buildLocaleUrl,
    hasOtherLocales,
  }
}
